"use client"

import { AppLogo } from "@/components/app-logo"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Footer } from "@/components/ui/footer"
import { useLanguage } from "@/lib/i18n/language-provider"
import { ArrowLeft, Tag } from "lucide-react"
import { useEffect, useState } from "react"
import changelogs from "@/data/changelogs.json"
import SmartLink from "@/components/smart-link";

interface ChangelogEntry {
  hash: string
  date: string
  message: string
  author: string
  version: string
}

export function BackButton() {
  return (
    <button
      onClick={() => {
        if (typeof window !== 'undefined' && window.location?.protocol === 'file:') {
          const pathname = window.location.pathname || '';
          const outIdx = pathname.lastIndexOf('/out/');
          const base = outIdx >= 0 ? pathname.substring(0, outIdx + '/out/'.length) : pathname;
          window.location.href = base + 'index.html';
        } else {
          if (window.history.length > 1) window.history.back();
          else window.location.href = '/';
        }
      }}
      aria-label="Retour"
      title="Retour"
      style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
    >
      ←
    </button>
  );
}

export default function ChangelogsPage() {
  const { t } = useLanguage()
  const [entries, setEntries] = useState<ChangelogEntry[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const timer = setTimeout(() => {
      setEntries(changelogs as ChangelogEntry[])
      setLoading(false)
    }, 250)
    return () => clearTimeout(timer)
  }, [])

  const latestVersion = entries[0]?.version

  return (
    <div className="min-h-screen bg-background text-foreground p-3 md:p-4 flex flex-col">
      <div className="max-w-4xl mx-auto w-full">
        {/* Header avec retour et titre */}
        <div className="border-b mb-4 md:mb-6">
          <div className="flex h-14 md:h-16 items-center px-2 md:px-4">
            <Button variant="ghost" size="icon" onClick={() => {
              if (typeof window !== 'undefined' && window.location?.protocol === 'file:') {
                const pathname = window.location.pathname || '';
                const outIdx = pathname.lastIndexOf('/out/');
                const base = outIdx >= 0 ? pathname.substring(0, outIdx + '/out/'.length) : pathname;
                window.location.href = base + 'index.html';
              } else {
                if (window.history.length > 1) window.history.back();
                else window.location.href = '/';
              }
            }} className="h-9 w-9 md:h-10 md:w-10">
              <ArrowLeft className="h-5 w-5 md:h-6 md:w-6" />
            </Button>
            <div className="ml-2 md:ml-4 flex items-center gap-2">
              <AppLogo />
              <h1 className="text-lg sm:text-xl md:text-2xl font-bold tracking-tight">
                {t("changelogs.title")}
              </h1>
            </div>
          </div>
        </div>

        <Card className="mb-6 md:mb-8">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <CardTitle className="text-lg md:text-xl">{t("changelogs.title")}</CardTitle>
                <CardDescription className="text-sm mt-1">{t("changelogs.description")}</CardDescription>
              </div>
              {latestVersion && (
                <span className="inline-flex items-center gap-1.5 bg-primary/10 text-primary border border-primary/20 px-3 py-1 rounded-full text-sm font-semibold self-start sm:self-auto flex-shrink-0">
                  <Tag className="h-3.5 w-3.5" />
                  Dernière version : v{latestVersion}
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-6 md:py-8">
                <div className="animate-spin rounded-full h-6 w-6 md:h-8 md:w-8 border-b-2 border-primary"></div>
                <span className="ml-2 md:ml-3 text-sm md:text-base">{t("changelogs.loading")}</span>
              </div>
            ) : (
              <div className="space-y-3 md:space-y-4">
                {entries.map((entry, index) => (
                  <div
                    key={`${entry.version}-${entry.hash}`}
                    className="flex gap-3 md:gap-4 border-l-2 border-primary/20 pl-3 md:pl-4 py-2 hover:border-primary transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-mono font-bold flex-shrink-0 ${
                          index === 0
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-primary/10 text-primary border border-primary/20'
                        }`}>
                          <Tag className="h-3 w-3" />
                          v{entry.version}
                        </span>
                        {index === 0 && (
                          <span className="text-[10px] bg-green-500/15 text-green-500 border border-green-500/20 px-1.5 py-0.5 rounded font-medium">
                            Dernière
                          </span>
                        )}
                        <span className="text-[10px] md:text-xs bg-muted px-2 py-0.5 rounded font-mono text-muted-foreground flex-shrink-0">
                          {entry.hash}
                        </span>
                      </div>
                      <p className="font-medium text-sm md:text-base break-words leading-snug">
                        {entry.message}
                      </p>
                      <div className="text-xs text-muted-foreground mt-1 flex flex-col sm:flex-row sm:justify-between gap-0.5">
                        <span className="truncate">{entry.author}</span>
                        <span className="flex-shrink-0">{entry.date}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex flex-col sm:flex-row justify-center items-center gap-3 mb-6 md:mb-8">
          <Button onClick={() => {
            if (typeof window !== 'undefined' && window.location?.protocol === 'file:') {
              const pathname = window.location.pathname || '';
              const outIdx = pathname.lastIndexOf('/out/');
              const base = outIdx >= 0 ? pathname.substring(0, outIdx + '/out/'.length) : pathname;
              window.location.href = base + 'index.html';
            } else {
              if (window.history.length > 1) window.history.back();
              else window.location.href = '/';
            }
          }} className="w-full sm:w-auto">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t("nav.back")}
          </Button>
          <Button variant="outline" asChild className="w-full sm:w-auto">
            <SmartLink href="/grid/">
              {t("nav.grid")}
            </SmartLink>
          </Button>
          <Button variant="outline" asChild className="w-full sm:w-auto">
            <SmartLink href="/about/">
              {t("nav.about")}
            </SmartLink>
          </Button>
        </div>
      </div>
      <Footer />
    </div>
  )
} 